/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Stack, Button, CircularProgress } from '@mui/material';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useAppSelector } from 'app/hooks';
import { getUserId } from 'utils/sessionStorage';
import counselorServices from 'services/counselorServices';
import PaymentServices from 'services/payment';
import { STRIPE_PRICE_NAME } from 'constants/common';
import { routers } from 'router/router';
import Cookies from 'js-cookie';

// Price data from StripePrices.tsx
const PARENT_UPGRADE_PRICES = {
  silver: {
    month: { amount: '24.99', priceName: STRIPE_PRICE_NAME.SilverPlusMonth },
    year: { amount: '204', priceName: STRIPE_PRICE_NAME.SilverPlusYear },
  },
  gold: {
    month: { amount: '79.00', priceName: STRIPE_PRICE_NAME.GoldMonth },
    year: { amount: '468.00', priceName: STRIPE_PRICE_NAME.GoldYear },
  },
};

const CheckIcon = () => (
  <CheckCircleOutlineIcon sx={{ fontSize: 20, color: '#4d88f0', flexShrink: 0 }} />
);

const ParentUpgradePage = () => {
  const history = useHistory();
  const location = useLocation();
  const personalStore = useAppSelector((store) => store.basicInformation.myBasicInformation);
  const userId = personalStore?.student_id ?? getUserId();
  const urlStudentId = new URLSearchParams(location.search).get('studentId') ?? '';
  const [children, setChildren] = useState<any[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<any | null>(null);
  const [isMonthly, setIsMonthly] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      return;
    }
    counselorServices.getPartner(userId).then((res: any) => {
      const list = res ?? [];
      setChildren(list);
      if (list.length) {
        const fromUrl = urlStudentId ? list.find((c: any) => String(c.student_id) === urlStudentId) : null;
        setSelectedStudent(fromUrl || list[0]);
      }
      setIsLoading(false);
      console.log('children: ', children);
    }).catch(() => setIsLoading(false));
  }, [userId, urlStudentId]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getUpgradePlanUrl = async (studentId:any, priceName:string) => {
    let returnUrl = `${window.location.origin}#/dashboard/upgrade?studentId=${studentId}`;
    let billUrl: any = await PaymentServices.getUserBillPortalSelectedPlanUrl(returnUrl, studentId, priceName);
    return billUrl;
  }

  const handleChoosePlan = async (priceName: string) => {
    if (!selectedStudent?.student_id) return;
    setIsSubmitting(true);
    try {
      const studentId = String(selectedStudent.student_id);
      const studentEmail = selectedStudent?.student_info?.email || selectedStudent?.email || '';
      Cookies.set('request_id', studentId);
      Cookies.set('userEmail', studentEmail);

      let billUrl:any = await getUpgradePlanUrl(studentId, priceName);
      if (billUrl) {
        window.location.href = `${billUrl}`;
      }
    } catch (err) {
      console.error('Checkout error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendUpgradeLinkClick = () => {
    history.push(
      routers.settings.path + `?active-tab=5${selectedStudent?.student_id ? `&studentId=${selectedStudent.student_id}` : ''}`
    );
  };

  const studentDisplayName = selectedStudent
    ? [selectedStudent?.student_info?.first_name, selectedStudent?.student_info?.last_name]
        .filter(Boolean)
        .join(' ') || 'Your Child'
    : 'Your Child';

  const silverPrice = isMonthly ? PARENT_UPGRADE_PRICES.silver.month : PARENT_UPGRADE_PRICES.silver.year;
  const goldPrice = isMonthly ? PARENT_UPGRADE_PRICES.gold.month : PARENT_UPGRADE_PRICES.gold.year;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, width: '100%' }}>
        <CircularProgress sx={{ color: '#4d88f0' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '900px',
        margin: '0 auto',
        padding: isMobile ? 2 : 4,
        pb: 6,
      }}
    >
      {/* Header */}
      <Stack alignItems="center" spacing={1} sx={{ mb: 4 }}>
        <SchoolOutlinedIcon sx={{ fontSize: 48, color: '#4d88f0' }} />
        <Box sx={{ fontSize: isMobile ? 24 : 32, fontWeight: 700, color: '#212529', textAlign: 'center' }}>
          Choose a Plan for {studentDisplayName}
        </Box>
        <Box sx={{ fontSize: 15, color: '#6c757d', textAlign: 'center', maxWidth: 500 }}>
          You are upgrading on behalf of your child. They will get immediate access after payment.
        </Box>
      </Stack>

      {/* Feature cards */}
      <Stack
        direction="column"
        spacing={2}
        sx={{ mb: 4 }}
        alignItems="center"
      >
        {[
          {
            icon: <VisibilityOutlinedIcon sx={{ fontSize: 24, color: '#4d88f0' }} />,
            text: 'View their full profile — college list, GPA, courses, and activities',
          },
          {
            icon: <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 24, color: '#4d88f0' }} />,
            text: 'Message their counselor directly through GradMap',
          },
          {
            icon: <StarBorderOutlinedIcon sx={{ fontSize: 24, color: '#4d88f0' }} />,
            text: 'Add SMS alerts (Gold plan) so you never miss a deadline',
          },
        ].map((item, i) => (
          <Box
            key={i}
            sx={{
              width: '100%',
              maxWidth: 700,
              p: 2,
              borderRadius: '12px',
              backgroundColor: '#fff',
              border: '1px solid #E5E7EB',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: '#e8edf8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {item.icon}
            </Box>
            <Box sx={{ fontSize: 14, color: '#374151', lineHeight: 1.5, whiteSpace: 'nowrap' }}>
              {item.text}
            </Box>
          </Box>
        ))}
      </Stack>

      {/* Billing toggle */}
      <Stack direction="row" justifyContent="center" sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            borderRadius: '10px',
            border: '1px solid #E5E7EB',
            overflow: 'hidden',
            backgroundColor: '#fff',
          }}
        >
          <Box
            component="button"
            onClick={() => setIsMonthly(true)}
            sx={{
              px: 3,
              py: 1.25,
              border: 'none',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              backgroundColor: isMonthly ? '#4d88f0' : 'transparent',
              color: isMonthly ? 'white' : '#6c757d',
              transition: 'all 0.2s',
            }}
          >
            Monthly
          </Box>
          <Box
            component="button"
            onClick={() => setIsMonthly(false)}
            sx={{
              px: 3,
              py: 1.25,
              border: 'none',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              backgroundColor: !isMonthly ? '#4d88f0' : 'transparent',
              color: !isMonthly ? 'white' : '#6c757d',
              transition: 'all 0.2s',
            }}
          >
            Annual Save ~15%
          </Box>
        </Box>
      </Stack>

      {/* Plan cards */}
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={3}
        justifyContent="center"
        alignItems="stretch"
        sx={{ mb: 4 }}
      >
        {/* Silver Plan */}
        <Box
          sx={{
            flex: 1,
            maxWidth: 400,
            backgroundColor: '#F9FAFB',
            borderRadius: '16px',
            border: '1px solid #E5E7EB',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'inline-block',
              px: 1.5,
              py: 0.5,
              borderRadius: '8px',
              backgroundColor: 'rgba(77, 136, 240, 0.12)',
              fontSize: 13,
              fontWeight: 600,
              color: '#4d88f0',
              alignSelf: 'flex-start',
              mb: 2,
            }}
          >
            Silver Plan
          </Box>
          <Box sx={{ fontSize: 28, fontWeight: 700, color: '#212529', mb: 0.5 }}>
            ${silverPrice.amount}/{isMonthly ? 'mo' : 'yr'}
          </Box>
          <Box sx={{ fontSize: 13, color: '#6c757d', mb: 2 }}>
            ${PARENT_UPGRADE_PRICES.silver.month.amount}/month or $
            {PARENT_UPGRADE_PRICES.silver.year.amount}/year
          </Box>
          <Box sx={{ fontSize: 14, color: '#374151', mb: 2 }}>
            Unlock your parent's full access and counselor messaging
          </Box>
          <Stack spacing={1} sx={{ flex: 1, mb: 2 }}>
            {[
              'Full read-only profile access',
              'College list and application tracking',
              'In-app counselor messaging',
              'Parent-student in-app messaging',
            ].map((f, i) => (
              <Stack key={i} direction="row" alignItems="center" spacing={1}>
                <CheckIcon />
                <Box sx={{ fontSize: 14, color: '#374151' }}>{f}</Box>
              </Stack>
            ))}
          </Stack>
          <Button
            variant="outlined"
            fullWidth
            disabled={isSubmitting || !selectedStudent}
            onClick={() => handleChoosePlan(silverPrice.priceName)}
            sx={{
              py: 1.5,
              borderRadius: '8px',
              borderColor: '#D1D5DB',
              color: '#374151',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                borderColor: '#9CA3AF',
                backgroundColor: '#F9FAFB',
              },
            }}
          >
            Choose Silver
          </Button>
        </Box>

        {/* Gold Plan */}
        <Box
          sx={{
            flex: 1,
            maxWidth: 400,
            backgroundColor: '#FFFBEB',
            borderRadius: '16px',
            border: '1px solid #FDE68A',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              px: 1.5,
              py: 0.5,
              borderRadius: '8px',
              backgroundColor: '#FEF3C7',
              fontSize: 12,
              fontWeight: 600,
              color: '#92400E',
            }}
          >
            Most Popular
          </Box>
          <Box
            sx={{
              display: 'inline-block',
              px: 1.5,
              py: 0.5,
              borderRadius: '8px',
              backgroundColor: 'rgba(77, 136, 240, 0.12)',
              fontSize: 13,
              fontWeight: 600,
              color: '#4d88f0',
              alignSelf: 'flex-start',
              mb: 2,
            }}
          >
            Gold Plan
          </Box>
          <Box sx={{ fontSize: 28, fontWeight: 700, color: '#212529', mb: 0.5 }}>
            ${goldPrice.amount}/{isMonthly ? 'mo' : 'yr'}
          </Box>
          <Box sx={{ fontSize: 13, color: '#6c757d', mb: 2 }}>
            ${PARENT_UPGRADE_PRICES.gold.month.amount}/month or $
            {PARENT_UPGRADE_PRICES.gold.year.amount}/year
          </Box>
          <Box sx={{ fontSize: 14, color: '#374151', mb: 2 }}>
            Everything in Silver plus SMS alerts so you never miss a deadline
          </Box>
          <Stack spacing={1} sx={{ flex: 1, mb: 2 }}>
            {[
              'Everything in Silver',
              'SMS text deadline alerts',
              'SMS when counselor messages',
              'Priority support',
            ].map((f, i) => (
              <Stack key={i} direction="row" alignItems="center" spacing={1}>
                <CheckIcon />
                <Box sx={{ fontSize: 14, color: '#374151' }}>{f}</Box>
              </Stack>
            ))}
          </Stack>
          <Button
            variant="contained"
            fullWidth
            disabled={isSubmitting || !selectedStudent}
            onClick={() => handleChoosePlan(goldPrice.priceName)}
            sx={{
              py: 1.5,
              borderRadius: '8px',
              backgroundColor: '#4d88f0',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#3d78e0',
              },
            }}
          >
            Choose Gold
          </Button>
        </Box>
      </Stack>

      {/* Footer actions */}
      <Stack alignItems="center" spacing={1} sx={{ mt: 4 }}>
        <Button
          variant="outlined"
          onClick={handleSendUpgradeLinkClick}
          sx={{
            borderRadius: '8px',
            borderColor: '#D1D5DB',
            color: '#374151',
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              borderColor: '#9CA3AF',
              backgroundColor: '#F9FAFB',
            },
          }}
        >
          Send Upgrade Link to My Child
        </Button>
        <Box
          component="span"
          onClick={() => history.push(routers.parentMainDashboard?.path ?? '/dashboard/children')}
          sx={{
            fontSize: 14,
            color: '#9CA3AF',
            cursor: 'pointer',
            textDecoration: 'underline',
            '&:hover': { color: '#6B7280' },
          }}
        >
          Remind me later
        </Box>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 2 }}>
          <Box sx={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: '#9CA3AF' }} />
          <Box sx={{ fontSize: 12, color: '#9CA3AF' }}>Prices shown in USD. Cancel anytime.</Box>
        </Stack>
      </Stack>

    </Box>
  );
};

export default ParentUpgradePage;
